/**
 * `redirect_to` URL-param propagation through the auth flows.
 *
 * The value is a base64-encoded internal path (starts with `/`) that survives
 * being passed through `?redirect_to=...` across LoginPage → RegisterPage →
 * ProfilePage → /invite, etc.
 *
 * The decoder REJECTS anything that doesn't begin with a single `/` so an
 * attacker cannot smuggle `javascript:`, `//evil.com`, `http://…` etc through
 * an invite link.
 */

const REDIRECT_PARAM = "redirect_to";

export function encodeRedirectTo(internalPath: string): string {
  // btoa accepts only latin1 → encode to UTF-8 bytes first so Thai paths
  // (unlikely but possible via query strings) survive.
  const utf8 = unescape(encodeURIComponent(internalPath));
  // URL-safe base64: replace + → -, / → _, and drop padding.
  return btoa(utf8).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function decodeRedirectTo(b64: string | null | undefined): string | null {
  if (!b64) return null;
  try {
    // Reverse URL-safe alphabet, then re-pad to multiple of 4.
    const standard = b64.replace(/-/g, "+").replace(/_/g, "/");
    const padded = standard + "===".slice((standard.length + 3) % 4);
    const decoded = atob(padded);
    const path = decodeURIComponent(escape(decoded));
    return isSafeInternalPath(path) ? path : null;
  } catch {
    return null;
  }
}

/**
 * True only for paths that begin with a single `/` and are not a
 * protocol-relative URL (`//evil.com`) or a known dangerous scheme.
 */
export function isSafeInternalPath(path: string): boolean {
  if (typeof path !== "string" || path.length === 0) return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path.startsWith("/\\")) return false;
  // Lowercase the head to catch e.g. `JavaScript:` after the leading slash
  // (some browsers parse `/javascript:` as a relative path but the embedded
  // scheme can still surface through onclick=).
  const lower = path.toLowerCase();
  if (lower.includes("javascript:") || lower.includes("data:") || lower.includes("vbscript:")) {
    return false;
  }
  return true;
}

/**
 * Read `redirect_to` from a URLSearchParams (e.g. from `useSearchParams()`).
 * Returns null when missing or invalid.
 */
export function getRedirectTo(params: URLSearchParams): string | null {
  return decodeRedirectTo(params.get(REDIRECT_PARAM));
}

/**
 * Append `?redirect_to=<encoded>` to a target path when a redirect chain is
 * in flight. No-op when `redirectTo` is null/empty OR when the value isn't a
 * safe internal path — defense-in-depth so an unsafe value never gets
 * base64-encoded into the URL even if a future caller passes user input.
 */
export function withRedirectTo(targetPath: string, redirectTo: string | null | undefined): string {
  if (!redirectTo) return targetPath;
  if (!isSafeInternalPath(redirectTo)) return targetPath;
  const encoded = encodeRedirectTo(redirectTo);
  const sep = targetPath.includes("?") ? "&" : "?";
  return `${targetPath}${sep}${REDIRECT_PARAM}=${encoded}`;
}

export const REDIRECT_TO_PARAM_NAME = REDIRECT_PARAM;

// ── Post-verification redirect backup ───────────────────────────────────────
//
// Email/password registration sends the user to /verify-email. The intended
// destination (e.g. an `/invite?token=…` link) is carried in the URL, but the
// /verify-email tab can be reloaded while the user clicks the email link, which
// would drop the query string. We additionally stash the destination in
// localStorage so the invite token is never lost across the round trip.

const POST_VERIFY_REDIRECT_KEY = "auth:post-verify-redirect";

/** Persist the post-verification destination (safe internal paths only). */
export function stashPostVerifyRedirect(path: string | null | undefined): void {
  if (typeof window === "undefined") return;
  if (!path || !isSafeInternalPath(path)) return;
  window.localStorage.setItem(POST_VERIFY_REDIRECT_KEY, path);
}

/** Read AND clear the stashed post-verification destination. */
export function takePostVerifyRedirect(): string | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(POST_VERIFY_REDIRECT_KEY);
  window.localStorage.removeItem(POST_VERIFY_REDIRECT_KEY);
  return v && isSafeInternalPath(v) ? v : null;
}
